import React from "react"
import { styled } from "@mui/material/styles"
import axios from "axios"
import _ from "lodash"
import Collection from "../models/collection"
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  SpeedDial,
  SpeedDialAction,
  Typography,
} from "@mui/material"
import { inject, observer } from "mobx-react"
import { MINIMUM_COLLECTION_SIZE } from "../utils/samplefocusDefaults"
import { getCsrfToken } from "../utils/userUtils"

const SFDialog = styled(Dialog)`
  padding: 24px;
`

const SFDialogTitle = styled(DialogTitle)`
  font-weight: bold;
  font-size: 1.5rem;
  padding: 8px 16px;
  letter-spacing: 1px;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.1);
`

const SFDialogActions = styled(DialogActions)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;  // to ensure wrapping if space is limited
`

const SFFormControl = styled(FormControl)`
  width: 100%;
  margin-top: 16px;
  margin-bottom: 24px;
`

const SFInputLabel = styled(InputLabel)`
  &.Mui-shrink {
    margin-top: -4px;
  }
`

const SFSelect = styled(Select)`
  padding-left: 8px;
  padding-right: 24px;
`

const SFList = styled(List)`
  max-height: 200px;
  overflow: auto;
  margin-bottom: 4px;
`

const SFListItem = styled(ListItem)`
  padding-left: 0;
  padding-right: 0;
`

const SFMenuItem = styled(MenuItem)`
  padding-left: 16px;
  padding-right: 16px;
`

const SFSpeedDial = styled(SpeedDial)`
  .MuiSpeedDial-fab {
    background-color: #F44336;
  }
`

const SFSpinnerContainer = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
`

@inject("sampleStore", "collectionStore", "snackbarStore")
@observer
export default class BatchEditReactButton extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      displayAddToCollectionModal: false,
      displayModalLoadingSpinner: false,
    }
  }

  handleCreateCollectionClick = async () => {
    const { sampleStore, snackbarStore } = this.props

    const selectedSamples = sampleStore.selectedSamples
    if (selectedSamples.length < MINIMUM_COLLECTION_SIZE) {
      snackbarStore.showSnackbar(`A collection needs at least ${MINIMUM_COLLECTION_SIZE} samples.`, "error")
      return
    }

    const collectionName = prompt("What should this collection be called?")
    if (collectionName === null || collectionName.length === 0) {
      snackbarStore.showSnackbar("A collection name is required", "error")
      return
    }

    const collection = new Collection(
      { name: collectionName, samples: selectedSamples }
    )
    try {
      await collection.create({ setToSampleOwner: true })
      snackbarStore.showSnackbar("Collection created succesfully.", "success")
    } catch (error) {
      snackbarStore.showSnackbar(`Something went wrong: ${ error.response.data.errors.join(" ")}.`, "error")
    }
  }

  handleAddToCollectionClick = async () => {
    const { collectionStore, sampleStore, snackbarStore } = this.props
    const selectedSamples = sampleStore.selectedSamples
    const uniqSelectedSamplesUserSlugs = _.uniq(selectedSamples.map(sample => sample.user_slug));

    axios.defaults.headers.common["Accept"] = "application/json"
    axios.defaults.headers.common["X-CSRF-Token"] = getCsrfToken()

    if (uniqSelectedSamplesUserSlugs.length === 1) {
      this.setState({displayAddToCollectionModal: true, displayModalLoadingSpinner: true})
      collectionStore.clearFocusedCollection()
      try {
        const userSlug = uniqSelectedSamplesUserSlugs[0]
        const userCollectionUrl = `/api/users/${userSlug}/collections`
        const response = await axios ({
          url: userCollectionUrl,
          responseType: "json",
        })
        response.data.collections.forEach(collectionData => {
          const collection = new Collection(collectionData)
          collectionStore.addUniqueAvailableCollection(collection);
        })
        this.setState({displayModalLoadingSpinner: false})
      } catch (error) {
        snackbarStore.showSnackbar(`Something went wrong: ${error.response.data.errors.join(" ")}.`, "error")
      }
    } else {
      snackbarStore.showSnackbar(`Samples must belong to the same user to create a collection. You've selected ${uniqSelectedSamplesUserSlugs.length} different user's samples`, "error")
    }
  }

  handleCloseModal = () => {
    const { collectionStore } = this.props
    this.setState({displayAddToCollectionModal: false})
    collectionStore.availableCollections = []
    collectionStore.clearFocusedCollection()
  }

  handleSelectCollection = (e) => {
    const { collectionStore } = this.props
    const selectedId = e.target.value
    collectionStore.setFocusedCollection(selectedId)
  }

  handleAddSelectedSamplesToCollection = async () => {
    const { collectionStore, sampleStore, snackbarStore, pageType } = this.props
    const didAssign = await collectionStore.focusedCollection.assignSamples(sampleStore.selectedSampleIds)

    if (didAssign) {
      this.setState({displayAddToCollectionModal: false})
      snackbarStore.showSnackbar(`Selected samples successfully added to ${collectionStore.focusedCollection.name}!`, "success")

      if (pageType === "userPage") {
        sampleStore.selectedSamples.forEach((sample) => {
          sampleStore.removeSample(sample)
        })
      }
      collectionStore.availableCollections = []
      collectionStore.clearFocusedCollection()
      sampleStore.clearStore()
    } else {
      snackbarStore.showSnackbar(`Something went wrong: ${error.response.data.errors.join(" ")}.`, "error")
    }
  }

  handleDeleteSamplesClick = async () => {
    const shouldDelete = confirm(
      "Are you sure you want to delete the selected samples?"
    )
    if (shouldDelete === false) {
      return
    }

    const { sampleStore } = this.props
    sampleStore.selectedSamples.forEach(async (sample) => {
      const didDelete = await sample.delete()
      if (didDelete) {
        sampleStore.removeSample(sample)
        sampleStore.deselectSample(sample)
      } else {
        console.error(`${sample.slug} could not be deleted.`)
      }
    })
  }

  renderAddToCollectionModal() {
    const { collectionStore, sampleStore } = this.props;
    const { displayAddToCollectionModal, displayModalLoadingSpinner } = this.state

    return (
      <SFDialog maxWidth="sm" fullWidth onClose={this.handleCloseModal} open={displayAddToCollectionModal}>
        <SFDialogTitle>
          Assign Samples to Collection
        </SFDialogTitle>
        {displayModalLoadingSpinner ? (
          <SFSpinnerContainer>
            <CircularProgress />
          </SFSpinnerContainer>
        ) : (
          <DialogContent>
          <SFFormControl>
            <SFInputLabel id="collections">
              Collections
            </SFInputLabel>
            <SFSelect
              label="collections"
              labelId="collections"
              id="select"
              onChange={this.handleSelectCollection}
            >
              {collectionStore.availableCollections.map((collection) => (
                <SFMenuItem value={collection.id} key={collection.slug}>
                  {collection.name}
                </SFMenuItem>
              ))}
            </SFSelect>
          </SFFormControl>

          <Typography variant="subtitle2" sx={{ marginBottom: 1, opacity: 0.7 }}>
            Selected Samples:
          </Typography>
          <SFList dense>
            {sampleStore.selectedSamples.map((sample, index) => (
            <SFListItem key={index}>
              <ListItemText>
                {sample.displayName}
              </ListItemText>
            </SFListItem>
            ))}
          </SFList>
        </DialogContent>
        )}

        <SFDialogActions>
          {collectionStore.focusedCollection ? (
            <Typography variant="body2" sx={{ opacity: 0.6, marginLeft: 1 }}>
              Assign {sampleStore.selectedSamples.length} samples to {collectionStore.focusedCollection.name}?
            </Typography>
          ) : (
            <Box flexGrow={1}></Box>
          )}
          <Button
            variant="contained"
            color="primary"
            onClick={this.handleAddSelectedSamplesToCollection}
            disabled={!collectionStore.focusedCollection}
          >
            Submit
          </Button>
        </SFDialogActions>
      </SFDialog>
    )
  }

  render() {
    const actions = [
      {
        icon: <i className="far fa-folder fa-2x" aria-hidden="true"></i>,
        name: "Create Collection",
        onClick: this.handleCreateCollectionClick,
      },
      {
        icon: <i className="far fa-folder-open fa-2x" aria-hidden="true"></i>,
        name: "Add to Collection",
        onClick: this.handleAddToCollectionClick,
      },
      {
        icon: <i className="far fa-trash-can fa-2x" aria-hidden="true"></i>,
        name: "Delete Samples",
        onClick: this.handleDeleteSamplesClick,
      },
    ]

    return (
      <>
        {this.renderAddToCollectionModal()}
        <SFSpeedDial
          ariaLabel="Batch edit actions - use up and down arrow keys to select actions"
          sx={{ position: "fixed", bottom: 42, right: 32 }}
          icon={<i className="far fa-pen-to-square fa-2x" aria-hidden="true"></i>}
        >
          {actions.map((action) => (
            <SpeedDialAction
              key={action.name}
              icon={action.icon}
              tooltipTitle={action.name}
              tooltipOpen
              onClick={action.onClick}
            />
          ))}
        </SFSpeedDial>
      </>
    )
  }
}
